import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { SEOHead } from '@/components/SEOHead';
import { PageMasthead } from '@/components/ui/PageMasthead';
import { PageNav } from '@/components/ui/PageNav';
import { MobileSectionNav } from '@/components/ui/MobileSectionNav';
import { useSectionSpy } from '@/hooks/useSectionSpy';
import {
  MemoEyebrow,
  SectionNum,
  EvidencePlate,
  FigureStressWindows,
  CtaPrimary,
  CtaGhost,
} from '@/components/Patterns';

/* ── Content (COPY-LOCKED — do not alter any visible sentence) ── */

/* ── Services rail — concise nav labels (owner-authorized abbreviations derived
   from the existing headings) + target section ids ── */

const SERVICES_RAIL = [
  { id: 'primary-engagement', num: '01', label: 'Primary Engagement' },
  { id: 'optional-modules', num: '02', label: 'Optional Analysis' },
  { id: 'engagement-01', num: '03', label: 'Pre Diagnostics' },
  { id: 'engagement-02', num: '04', label: 'Diagnostics' },
  { id: 'engagement-03', num: '05', label: 'Ongoing Engagements' },
  { id: 'engagement-04', num: '06', label: 'How We Decide' },
];

const SERVICES_RAIL_IDS = SERVICES_RAIL.map((s) => s.id);

const EngagementHeader = ({ label, title, isOpen, onToggle }) => (
  <button
    onClick={onToggle}
    aria-expanded={isOpen}
    className="w-full flex items-center justify-between gap-6 text-left py-6 md:py-7 group"
  >
    <div className="flex items-baseline gap-4 md:gap-8">
      <SectionNum>{label}</SectionNum>
      <h3
        className="font-display text-xl md:text-2xl font-medium tracking-tight"
        style={{ color: 'var(--memo-ink)' }}
      >
        {title}
      </h3>
    </div>
    <span
      aria-hidden="true"
      className="flex-shrink-0 border border-memo-line rounded px-1.5 py-0.5 text-memo-muted"
    >
      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
    </span>
  </button>
);

const BulletItem = ({ text }) => (
  <li className="flex items-start gap-2.5">
    <span className="text-memo-signal flex-shrink-0 leading-relaxed" aria-hidden="true">—</span>
    <span className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>{text}</span>
  </li>
);

const PolicyLine = ({ text }) => (
  <p className="text-sm leading-relaxed pl-5 border-l border-memo-line-2 text-memo-muted">{text}</p>
);

const SubSectionLabel = ({ text }) => (
  <p className="font-mono text-xs tracking-[0.18em] uppercase text-memo-muted mb-3 mt-6">{text}</p>
);

/* Indent accordion body content under the marginal-numbered title column */
const AccordionBody = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-[3.5rem_1fr] gap-0 md:gap-5 px-1 pb-12">
    <div aria-hidden="true" />
    <div className="space-y-8 max-w-[68ch] xl:max-w-[80ch] pt-1">{children}</div>
  </div>
);

export function Services() {
  // First engagement accordion starts open by default (never a wall of closed labels).
  const [openEngagement, setOpenEngagement] = useState(1);
  const toggleEng = (n) => setOpenEngagement(openEngagement === n ? null : n);
  const activeId = useSectionSpy(SERVICES_RAIL_IDS, '0px 0px -80% 0px');

  const modules = [
    { name: 'Extended Forecast Decomposition', tier: 'Module A', desc: 'Detailed analysis of volume and pattern components breakdown on a longer timeframe. Needs 6+ months of interval data.' },
    { name: 'Multi Queue Topology Mapping', tier: 'Module B', desc: 'Crossqueue routing and overflow in multiskill environments. Interval data needed per queue.' },
    { name: 'Adherence Attribution Audit', tier: 'Module C', desc: 'Systematic recategorization of the adherence states based on what has actually occurred. Needs AUX code level data.' },
    { name: 'Shrinkage Recalibration', tier: 'Module D', desc: 'Shrinkage category remapping and quantification of historic errors. Planned versus actual headcount data will be needed' },
    { name: 'Intraday Response Latency Analysis', tier: 'Module E', desc: 'Characterization of management response behavior relative to signal emergence. Timestamped reforecast data is needed.' },
    { name: 'Data Sufficiency Roadmap', tier: 'Module F', desc: 'Definition of further data collection needs for enhanced classification. From the OnPoint Diagnostics information.' },
  ];

  return (
    <div style={{ backgroundColor: 'var(--memo-field)' }}>
      <SEOHead />

      {/* ── Compact masthead ── */}
      <PageMasthead
        wide
        field="page"
        fieldFocal="0.78,0.24"
        eyebrow="Service Catalog"
        title="Services"
        intro="Every engagement is scoped to facts and data. The base offering is OnPoint Diagnostics, with optional analysis modules that can be scoped after it is complete."
      />

      {/* ── Services index + content (lg: nav | canvas) ── */}
      <div className="container-site spine-shell lg:grid lg:grid-cols-[16rem_minmax(0,1fr)]">
        <PageNav items={SERVICES_RAIL} activeId={activeId} label="Services sections" />

        <div className="lg:min-w-0 lg:border-l lg:border-memo-line lg:pl-10">
          <MobileSectionNav items={SERVICES_RAIL} activeId={activeId} label="Services sections" />

      {/* ── Primary Engagement — memorandum frame ── */}
      <section id="primary-engagement" className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div className="max-w-4xl">
          <MemoEyebrow>Primary Engagement</MemoEyebrow>

          <div className="mt-6 border border-memo-line-2 bg-memo-paper">
            <div className="px-6 md:px-8 py-3 border-b border-memo-line">
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-memo-signal">Base Engagement</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 items-start p-6 md:p-10">
              <div>
                <h2
                  className="font-display text-2xl md:text-3xl font-medium tracking-tight"
                  style={{ color: 'var(--memo-ink)' }}
                >
                  OnPoint Diagnostics
                </h2>
                <p className="mt-5 text-[0.95rem] leading-relaxed max-w-xl" style={{ color: 'var(--memo-ink-2)' }}>
                  Structural instability classification for one workforce system environment. Fixed scope, fixed output. Completed in 3 weeks from confirmed export receipt after Pre Diagnostics Session.
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm" style={{ color: 'var(--memo-ink-2)' }}>
                  <span>One environment</span>
                  <span className="text-memo-faint">·</span>
                  <span>3 weeks</span>
                  <span className="text-memo-faint">·</span>
                  <span>PDF memorandum + annexes</span>
                  <span className="text-memo-faint">·</span>
                  <span>14 days data retention</span>
                </div>
              </div>
              <div className="lg:pt-2">
                <CtaPrimary to="/rapid-diagnostic" testid="services-rapid-diag-link">Details</CtaPrimary>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Optional Analysis Modules — memorandum matrix ── */}
      <section id="optional-modules" className="py-16 md:py-20 border-b border-memo-line">
        <div>
          <MemoEyebrow>Optional Analysis Modules</MemoEyebrow>
          <p className="mt-4 text-lg leading-relaxed max-w-[62ch]" style={{ color: 'var(--memo-ink-2)' }}>
            Open to scoping following completion of OnPoint Diagnostics. Modules are scoped independently depending on data availability and engagement targets.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 border-t border-memo-line-2">
            {modules.map((mod, i) => (
              <div
                key={mod.tier}
                data-testid={`service-module-${mod.tier.replace(/\s+/g, '-').toLowerCase()}`}
                className={`px-6 py-7 border-b border-memo-line ${i % 2 === 0 ? 'md:border-r' : ''} ${
                  i % 3 === 2 ? 'xl:border-r-0' : 'xl:border-r'
                }`}
              >
                <div className="flex items-baseline gap-3 justify-between">
                  <span className="font-mono text-xs tracking-[0.14em] uppercase text-memo-signal">{mod.tier}</span>
                  <h3 className="font-display text-lg font-medium text-right">{mod.name}</h3>
                </div>
                <p className="mt-3 text-[0.9rem] leading-relaxed text-memo-muted">{mod.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Engagement Types — full-width memorandum accordions ── */}
      <section className="py-16 md:py-20 bg-memo-paper border-b border-memo-line">
        <div>
          <div className="mb-12 max-w-3xl">
            <MemoEyebrow>Engagement Types</MemoEyebrow>
            <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3">
              How We Engage as a WFM Analytics Lab
            </h2>
            <p className="mt-5 text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
              Our engagement model is comprised of three levels: Pre Diagnostics Session that scopes the problem and verifies fit, followed by an engagement with our OnPoint Diagnostics capability which is both a standalone or a precursor to an ongoing structural partnership.
            </p>
          </div>

          <div className="border-t border-memo-line-2">
            {/* 1. Pre Diagnostics Call */}
            <div id="engagement-01" className="border-b border-memo-line">
              <EngagementHeader label="01" title="Pre Diagnostics Session | Structural Instability Intake" isOpen={openEngagement === 1} onToggle={() => toggleEng(1)} />
              {openEngagement === 1 && (
                <AccordionBody>
                  <div>
                    <SubSectionLabel text="Purpose" />
                    <p className="text-[0.95rem] leading-relaxed mb-3" style={{ color: 'var(--memo-ink-2)' }}>
                      A focused intake designed to answer a single question:
                    </p>
                    <blockquote className="pl-5 border-l-2 border-memo-signal text-[0.95rem] italic leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                      "Is what you are experiencing an execution issue, or a structural WFM anomaly in demand, coverage, or behavior?"
                    </blockquote>
                    <p className="text-[0.95rem] leading-relaxed mt-4 mb-2" style={{ color: 'var(--memo-ink-2)' }}>We use this session to:</p>
                    <ul className="space-y-1.5">
                      {[
                        'Map your current WFM environment (tools, queues, geos, channels, SLAs).',
                        'Identify which areas of instability are being experienced (operations, customer, finance, HR) and how they manifest (e.g., "we hit the numbers but it still feels unstable", "we have random spikes that never feel random to the frontline", "our cost per contact is all over the place").',
                        'Determine which data exports are possible for use in an OnPoint Diagnostics.',
                        'Confirm whether our approach is what you are looking for, or whether a different path is better.',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                  </div>

                  <div>
                    <SubSectionLabel text="Format & Participants" />
                    <ul className="space-y-1.5">
                      {[
                        '60 to 90 min remote.',
                        'Head of Operations / Site Lead',
                        'WFM / Planning Lead',
                        'Optional: Finance / CX lead (for cost-, and experience framing)',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                  </div>

                  <div>
                    <SubSectionLabel text="What We Look For (Without Yet Touching Data)" />
                    <ul className="space-y-1.5">
                      {[
                        'Recurring stress periods (repeat in same time, same days, repeat in same queues).',
                        '"We hit the numbers but it still feels unstable."',
                        'Forecast error that is "okay in total" but not at the interval level.',
                        'Complaints around "micro peak intervals" or "random peaks" that never feel random to the frontline.',
                        'Cost per contact / FTE narratives not reflecting operational stories.',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                  </div>

                  <div>
                    <SubSectionLabel text="Outcome of the Call" />
                    <p className="text-[0.95rem] leading-relaxed mb-2" style={{ color: 'var(--memo-ink-2)' }}>By the end of the Pre Diagnostics Call you get:</p>
                    <ul className="space-y-1.5 mb-4">
                      {[
                        'A clear yes or no on whether an OnPoint Diagnostics will add structural clarity.',
                        'A scoped Diagnostic plan: which domains to analyze first, and which queues, channels, and time period.',
                        'The exact data export check list tailored to your needs',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                    <PolicyLine text="During the intake review we assess whether the provided data can support structural analysis.
This review constitutes part of the diagnostic service and does not guarantee suitability for further engagement." />
                  </div>
                </AccordionBody>
              )}
            </div>

            {/* 2. OnPoint Diagnostics */}
            <div id="engagement-02" className="border-b border-memo-line">
              <EngagementHeader label="02" title="OnPoint Diagnostics | Operational Instability Snapshot" isOpen={openEngagement === 2} onToggle={() => toggleEng(2)} />
              {openEngagement === 2 && (
                <AccordionBody>
                  <div>
                    <SubSectionLabel text="Objective" />
                    <p className="text-[0.95rem] leading-relaxed mb-2" style={{ color: 'var(--memo-ink-2)' }}>Present a brief, defensible snapshot of where your workforce system is structurally misaligned with regard to:</p>
                    <ul className="space-y-1.5">
                      {[
                        'Demand & Forecast Structure (shape, residuals, volatility)',
                        'Coverage & Schedule Architecture (distribution vs demand geometry)',
                        'Intraday Volatility & Queue Dynamics (stress periods & elasticity)',
                        'Workforce Behavior & Drift — how adherence and breaks affect outcomes at the interval level',
                        'Cost & Structural Risk — the volatility tax, mispriced capacity, and related effects',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                  </div>

                  <div>
                    <SubSectionLabel text="Data Inputs (Exports Only)" />
                    <p className="text-[0.95rem] leading-relaxed mb-2" style={{ color: 'var(--memo-ink-2)' }}>We work entirely on exported files:</p>
                    <ul className="space-y-1.5">
                      {[
                        'Interval level forecast vs actual (6-10 weeks where possible)',
                        'Schedule & coverage exports (planned vs actual by interval)',
                        'State logs (adherence / shrinkage / breaks)',
                        'Queue metrics (ASA, SLA, abandons, queue depth)',
                        'Cost / CPC / paid vs productive hours (if in scope)',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                  </div>

                  <div>
                    <SubSectionLabel text="Analytical Approach" />
                    <p className="text-[0.95rem] leading-relaxed mb-2" style={{ color: 'var(--memo-ink-2)' }}>We treat your environment as a structural system under pressure, not a dashboard problem:</p>
                    <ul className="space-y-1.5">
                      {[
                        'Rebuild demand and coverage geometry from raw interval level WFM data.',
                        'Measure the directional bias, the density compression and tail volatility.',
                        'Map recurring stress intervals and elasticity breakdowns.',
                        'Recognize when behaviors cluster (e.g., break synchronization or micro-drift) and distinguish structural amplification from individual fault.',
                        'Break down cost exposure into macro versus structural elements.',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                  </div>

                  <div>
                    <SubSectionLabel text="Deliverables" />
                    <div className="space-y-8">
                      <div className="pl-5 border-l border-memo-line">
                        <p className="font-mono text-xs tracking-widest uppercase text-memo-muted mb-2">1. Operational Instability Snapshot</p>
                        <ul className="space-y-1.5">
                          {[
                            'Heatmaps of structural distortion across domains.',
                            'A heat map showing the structural distortion across the different domains.',
                            'Fragility maps at the interval level (where the instability comes from and not where it manifests itself).',
                          ].map(t => <BulletItem key={t} text={t} />)}
                        </ul>
                      </div>
                      <div className="pl-5 border-l border-memo-line">
                        <p className="font-mono text-xs tracking-widest uppercase text-memo-muted mb-2">2. Executive Brief (2–4 pages)</p>
                        <ul className="space-y-1.5">
                          {[
                            'A plain-language account of what is factually true about the mechanics of your operation',
                            '3-5 clear “structural moves” that would actually enhance the stability without resorting to “hire more people” or “buy another tool”.',
                          ].map(t => <BulletItem key={t} text={t} />)}
                        </ul>
                      </div>
                      <div className="pl-5 border-l border-memo-line">
                        <p className="font-mono text-xs tracking-widest uppercase text-memo-muted mb-2">3. Read-Out Session (90 minutes)</p>
                        <ul className="space-y-1.5">
                          {[
                            'Walkthrough with WFM + Ops leadership.',
                            'Q&A on tradeoffs, constraints, and sequencing.',
                            'Decision point on continued engagement: episodic snapshot only or sustained structural support.',
                          ].map(t => <BulletItem key={t} text={t} />)}
                        </ul>
                      </div>
                    </div>
                    <p className="text-[0.9rem] leading-relaxed text-memo-muted mt-6">
                      OnPoint Diagnostics is stand-alone: you can stop there and still walk away with a defensible structural read of your organization.
                    </p>

                    <EvidencePlate tag="Evidence · recurring stress windows">
                      <FigureStressWindows />
                    </EvidencePlate>
                  </div>
                </AccordionBody>
              )}
            </div>

            {/* 3. Ongoing Engagement */}
            <div id="engagement-03" className="border-b border-memo-line">
              <EngagementHeader label="03" title="Ongoing Engagements | From Snapshot to Stewardship" isOpen={openEngagement === 3} onToggle={() => toggleEng(3)} />
              {openEngagement === 3 && (
                <AccordionBody>
                  <p className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                    Once the OnPoint Diagnostics is complete, you have a choice: treat the atlas as a one-off snapshot or turn it into a structural guardrail that keeps instability from creeping back as demand, product and workforce change.
                  </p>
                  <p className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>Our ongoing model is deliberately light on theatrics and heavy on structural continuity. Both patterns are built on the same principles:</p>
                  <div className="space-y-2.5">
                    <PolicyLine text="No new dashboards." />
                    <PolicyLine text="No platform reimplementation." />
                    <PolicyLine text="File based analysis in an isolated analysis environment." />
                    <PolicyLine text="Clear boundaries between WFM ownership and lab interpretation." />
                  </div>

                  {/* 3.1 */}
                  <div className="border border-memo-line bg-memo-paper px-6 py-6">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="font-mono text-sm tracking-[0.18em] text-memo-signal">3.1</span>
                      <h4 className="font-display text-lg font-medium">Structural Stability Monitor</h4>
                    </div>
                    <p className="text-sm leading-relaxed text-memo-muted mb-5">Low Friction Ongoing Oversight: best for environments that are relatively stable but expensive to let drift.</p>
                    <SubSectionLabel text="Cadence" />
                    <ul className="space-y-1.5 mb-5">
                      {[
                        'Monthly or quarterly structural refresh based on updated exports.',
                        '1 session per cycle with WFM + Ops leadership (+ finance if cost exposure is in scope).',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                    <SubSectionLabel text="What We Do" />
                    <ul className="space-y-1.5 mb-5">
                      {[
                        'Rerun key structural checks on fresh data (density compression, coverage alignment, stress intervals, behavioral clustering, volatility exposure).',
                        'Track movement in core indices (e.g., bias index, elasticity slope, cost volatility).',
                        'Flag new risks when product, policy, or demand changes.',
                        'Maintain a living "structural change log" so leadership can see how interventions have shifted instability over time.',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                    <SubSectionLabel text="Your Outcomes" />
                    <ul className="space-y-1.5">
                      {[
                        'Early warning when slow drift is turning into structural fragility.',
                        'Independent structural view that sits alongside, not inside your BAU reporting.',
                        "A recurring, data-grounded checkpoint that supports WFM's internal story to execs.",
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                  </div>

                  {/* 3.2 */}
                  <div className="border border-memo-line bg-memo-paper px-6 py-6">
                    <div className="flex items-baseline gap-3 mb-2">
                      <span className="font-mono text-sm tracking-[0.18em] text-memo-signal">3.2</span>
                      <h4 className="font-display text-lg font-medium">Embedded WFM Analytics Lab</h4>
                    </div>
                    <p className="text-sm leading-relaxed text-memo-muted mb-5">High Engagement Structural Partner, best for highly changing environments (new markets, demand, automated systems, multiskill networks).</p>
                    <SubSectionLabel text="Cadence" />
                    <ul className="space-y-1.5 mb-5">
                      {[
                        'Monthly, bi-weekly or weekly analytical check ups.',
                        'Scenario based working sessions with WFM, Ops, and (where relevant) Product / Finance.',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                    <SubSectionLabel text="What We Do" />
                    <ul className="space-y-1.5 mb-5">
                      {[
                        'Cover the codesign process and shift experiments before launching them live.',
                        'Stresstest intended changes (e.g. automation, routing logic, policy changes) to the structural instability map.',
                        'Run a few selective micro services on “newly problematic queues” without reinventing the wheel.',
                        'Help WFM build the internal narrative: what moved performance, which changes were structural versus executional, and how to present it to leadership.',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                    <SubSectionLabel text="Your Outcomes" />
                    <ul className="space-y-1.5">
                      {[
                        'An external WFM analytics partner who speaks your language and supports your operation structurally, not politically.',
                        'Rapid & clean decisions about intervention, which levers to push, in which order, what results to expect under stress.',
                        'Structural integrity: your system is monitored as a whole, not as a set of unrelated KPIs and projects.',
                      ].map(t => <BulletItem key={t} text={t} />)}
                    </ul>
                  </div>
                </AccordionBody>
              )}
            </div>

            {/* 4. How We Decide */}
            <div id="engagement-04" className="border-b border-memo-line">
              <EngagementHeader label="04" title="How We Decide Which Path Fits" isOpen={openEngagement === 4} onToggle={() => toggleEng(4)} />
              {openEngagement === 4 && (
                <AccordionBody>
                  <p className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>
                    We don’t force a retainer. After the OnPoint Diagnostics we jointly decide:
                  </p>
                  <ul className="space-y-1.5">
                    {[
                      'Is the remaining risk structural enough to warrant ongoing oversight, or can your team absorb it internally?',
                      'What\u2019s the rate of change in your environment? (weekly change, quarterly campaigns).',
                      'Where is it hurting the most? (instability, expense, existing workforce tension, executive narrative)',
                    ].map(t => <BulletItem key={t} text={t} />)}
                  </ul>
                  <p className="text-[0.95rem] leading-relaxed" style={{ color: 'var(--memo-ink-2)' }}>From there we either:</p>
                  <div className="space-y-2.5">
                    <PolicyLine text="Finish with a standalone snapshot and straightforward recommendations or" />
                    <PolicyLine text="Formalize a Structural Stability Monitor (SSM) or Embedded WFM Analytics Lab engagement to provide a rhythm and range of work which resonates with your operational reality." />
                  </div>
                </AccordionBody>
              )}
            </div>
          </div>

          {/* ── Scoping note + sign-off CTAs ── */}
          <div className="mt-12 border-t border-memo-line-2 pt-10">
            <p className="text-sm leading-relaxed" style={{ color: 'var(--memo-muted)' }}>
              Module pricing and scope are determined after OnPoint Diagnostics completion. Contact us or begin the intake protocol to initiate scoping.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaPrimary to="/intake" testid="services-intake-cta">Begin Intake Protocol</CtaPrimary>
              <CtaGhost to="/contact">Contact</CtaGhost>
            </div>
          </div>
        </div>
      </section>
        </div>
      </div>
    </div>
  );
}
